import { useForm } from "react-hook-form";
import {
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useUpdateRoleMutation,
} from "app/services/api/rolesApi";
import { CreateRoleRequest } from "app/services/api/types/roles/requests/CreateRoleRequest";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { ToastModel } from "ui/types/ToastModel";
import { RoleModel } from "app/services/api/types/roles/models/RoleModel";
import { DeleteRoleRequest } from "app/services/api/types/roles/requests/DeleteRoleRequest";
import { useEffect } from "react";

export const useHandleRoleForm = (
  onSuccess?: () => void,
  initialRole?: RoleModel | null,
  currentTitle?: string,
) => {
  const { showNewToast } = useDisplayToast();
  const [triggerCreateRole, { isLoading: isCreateLoading }] =
    useCreateRoleMutation();
  const [triggerUpdateRole, { isLoading: isUpdateLoading }] =
    useUpdateRoleMutation();
  const [triggerDeleteRole, { isLoading: isDeleting }] =
    useDeleteRoleMutation();

  const { control, handleSubmit, setValue } = useForm<CreateRoleRequest>({
    defaultValues: {
      display_name: initialRole?.display_name || "",
      parent_id: initialRole?.parent?.id || null,
      permissions: initialRole?.permissions?.map((p) => p.id) ?? [],
    },
  });

  useEffect(() => {
    if (currentTitle) {
      setValue("display_name", currentTitle);
    }
  }, [currentTitle, setValue]);

  const handleToast = (toast: ToastModel) => {
    showNewToast(toast);
  };

  const handleCreateRole = async (formData: CreateRoleRequest) => {
    try {
      const transformedData: CreateRoleRequest = {
        ...formData,
        parent_id: Array.isArray(formData.parent_id)
          ? formData.parent_id[0]
          : formData.parent_id,
      };
      await triggerCreateRole(transformedData).unwrap();
      onSuccess?.();
      handleToast({
        id: "role-created",
        title: "New role has been created!",
        message: "The role was successfully added to the system.",
        type: "success",
      });
    } catch (e) {
      handleToast({
        id: "role-creation-error",
        title: "Something went wrong",
        message: e?.data?.message || "An unexpected error occurred.",
        type: "error",
      });
      console.error("Error creating role:", e);
    }
  };

  const handleUpdateRole = async (
    formData: CreateRoleRequest,
    roleId: number,
  ) => {
    try {
      await triggerUpdateRole({ role: roleId, body: formData }).unwrap();
      onSuccess?.();

      handleToast({
        id: "role-updated",
        title: "Role has been updated!",
        message:
          "The role was successfully updated to the system. Please verify their details from the table below.",
        type: "success",
      });
    } catch (e) {
      handleToast({
        id: "role-update-error",
        title: "Something went wrong",
        message: e?.data?.message || "An unexpected error occurred.",
        type: "error",
      });
      console.error("Error updating role:", e);
    }
  };

  const handleRoleSubmission = async (
    formData: CreateRoleRequest,
    initialRole?: RoleModel,
  ) => {
    if (initialRole?.id) {
      await handleUpdateRole(formData, initialRole.id);
    } else {
      await handleCreateRole(formData);
    }
  };

  const submitForm = handleSubmit(async (formData: CreateRoleRequest) => {
    handleRoleSubmission(formData, initialRole ?? undefined)
      .then(onSuccess)
      .catch(() => {});
  });

  const handleDeleteRole = async ({ id }: Pick<RoleModel, "id">) => {
    try {
      const body: DeleteRoleRequest = { name: initialRole?.display_name };
      await triggerDeleteRole({ role: id, body });
      onSuccess?.();
      showNewToast({
        id: `role-deleted-${id}`,
        title: "Role deleted successfully",
        message: "The role has been deleted successfully",
        type: "success",
      });
    } catch (e) {
      handleToast({
        id: "role-deletion-error",
        title: "Something went wrong",
        message: e?.data?.message || "An unexpected error occurred.",
        type: "error",
      });
    }
  };

  return {
    control,
    handleSubmit,
    submitForm,
    isLoadingAction: isDeleting || isCreateLoading || isUpdateLoading,
    handleDeleteRole,
  };
};
